import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  Component,
  DestroyRef,
  Inject,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { ContactInfo, FilterContacts } from '@app-types/api/contact';
import { ContactClient } from '@app-services/api/clients/contact.client';
import {
  SETTING_SETTINGS,
  defaultFilter,
} from '@app-shared/constants/constants';
import { TagContract } from '@app-types/api/tag';
import { TagType } from '@app-types/enums/tags';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SomethingWentWrongComponent } from '../../common/error/something-went-wrong/something-went-wrong.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { InfinityScrollComponent } from '../../common/infinity-scroll/infinity-scroll.component';
import { NoDataComponent } from '../../common/no-data/no-data.component';
import { TagComponentComponent } from '../../common/tag-component/tag-component.component';
import { EmailSearchTagInputComponent } from '../../email-search-advanced-dialog/email-search-tag-input/email-search-tag-input.component';

const { STEP_FETCH } = SETTING_SETTINGS;

@Component({
  selector: 'app-email-contacts-dialog',
  templateUrl: './email-contacts-dialog.component.html',
  styleUrls: ['./email-contacts-dialog.component.scss'],
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'select-overlay-pane' },
    },
  ],
  imports: [
    MatDialogModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    NgxSkeletonLoaderModule,
    SomethingWentWrongComponent,
    MatTableModule,
    MatCheckboxModule,
    MatInputModule,
    InfinityScrollComponent,
    NoDataComponent,
    TagComponentComponent,
    EmailSearchTagInputComponent,
  ],
})
export class EmailContactsDialogComponent implements OnInit {
  public isLoading = true;
  public isLoadingScroll = false;
  public canNavigateForward = false;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;

  public mailAccountId: number;
  public contacts: ContactInfo[] = [];
  public dataSource = new MatTableDataSource<ContactInfo>(this.contacts);
  public displayedColumns: string[] = ['select', 'alias', 'email', 'label'];
  public stepFetch = STEP_FETCH;
  public filter: FilterContacts = { ...defaultFilter, tags: [] };
  public selectedTags = new Set<TagContract>();
  public readonly tagType = TagType.Contact;
  public offset = 0;
  public selection = new SelectionModel<ContactInfo>(true, []);
  public myControl = new UntypedFormControl();
  public options: string[] = [];
  public isError = false;
  public isErrorScroll = false;
  public search = '';

  private destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<EmailContactsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private matchError: MatchError,
    private contactClient: ContactClient,
    public dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.close();
      }
    });
    this.mailAccountId = this.data.mailAccountId;

    this.myControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.onSearch();
      });

    await this.loadContactsList(true);
  }

  public resetScrollData(): void {
    this.offset = 0;
  }

  public async onSearch(): Promise<void> {
    this.resetScrollData();
    this.search = (this.myControl.value ?? '').toString().trim();
    this.infinityScroll?.scrollTop();
    await this.loadContactsList(true);
  }

  public async onTagsFilterChange(tags: TagContract[]): Promise<void> {
    this.filter = {
      ...this.filter,
      tags,
    };
    await this.onSearch();
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.stepFetch;
    await this.loadContactsList(false);
  }

  async refreshData(): Promise<void> {
    await this.loadContactsList(false);
  }

  private rebuildOptionsFromContacts(): void {
    const emails = this.contacts
      .map(c => c.emails?.find(e => e.isDefault)?.email ?? c.emails?.[0]?.email)
      .filter((e): e is string => typeof e === 'string' && e.length > 0);
    this.options = [...new Set(emails)];
  }

  private async loadContactsList(reset: boolean): Promise<void> {
    if (reset) {
      this.resetScrollData();
    }
    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.contacts.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;

    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const trimmed = this.search?.trim();
      const nullableSearchValue = trimmed ? trimmed : null;
      const { tags: _, ...rest } = this.filter;
      const filter = {
        ...rest,
        tagIds: this.filter?.tags?.length
          ? this.filter.tags.map(el => el.tagId)
          : [],
      };
      const request = {
        mailAccountId: this.mailAccountId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter,
        search: nullableSearchValue,
      };
      const response = await this.contactClient.searchForAccount(request);

      const batch = response.data.map(contact => ({
        ...contact,
        defaultEmail:
          contact.emails.find(email => email.isDefault)?.email ??
          contact.emails[0].email,
      }));
      this.contacts = this.offset === 0 ? batch : [...this.contacts, ...batch];
      this.dataSource.data = this.contacts;
      this.rebuildOptionsFromContacts();
      this.canNavigateForward = batch.length === this.stepFetch;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
        this.matchError.logError(e);
      } else {
        this.isError = true;
        this.matchError.errorHandler(e);
        this.matchError.logError(e);
      }
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public async retryLoad(): Promise<void> {
    this.isError = false;
    await this.onSearch();
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 100);
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  public addContact(email: string): void {
    this.dialogRef.close([email]);
  }

  public addSelectedContacts(): void {
    const emails = this.selection.selected.map(
      e => e.emails.find(el => el.isDefault)?.email ?? e.emails[0].email
    );
    this.dialogRef.close(emails);
  }

  public close(): void {
    this.dialogRef.close();
  }
}
